import * as React from 'react';
import {
  isAdhocGroupSectionKey,
  isAdhocSectionKey,
  type ScopeMeta,
  type SalesDealEstimateResult,
} from '@protoss/shared';
import { RichTextEditor, SkeletonBlock, type Editor, type ScopePropertyApi } from '@protoss/ui';

import type {
  SalesDealQuestionnaireSummary,
  SalesDealTemplateFieldSummary,
} from '../services/salesApi';
import { buildHoursByField } from '../utils/estimateHours';
import { fieldHasVisibleContent } from '../utils/field-display-value';
import { useQuestionnaireAutocomplete } from '../hooks/useQuestionnaireAutocomplete';
import { ScopeDocOutline } from './ScopeDocOutline';
import { ScopeItemMetaRow, type ScopeItemMetaKey } from './ScopeItemMetaRow';
import { ScopePropertyRow } from './ScopePropertyRow';
import { buildScopeSlashMenu } from '../utils/scope-doc-slash';
import {
  buildScopeDoc,
  collectScopeDocLockedHeadings,
  parseScopeDocKey,
} from '../utils/scope-doc';
import type {
  QuestionnaireAnswerMap,
  QuestionnaireFieldLookup,
} from '../utils/questionnaire-field-visibility';

export interface DealScopePaneProps {
  questionnaire: SalesDealQuestionnaireSummary | null;
  answers: QuestionnaireAnswerMap;
  fieldLookup: QuestionnaireFieldLookup;
  /** Saved estimate (effective data) so scope-item chips can show per-item hours; null = no chip. */
  estimate?: SalesDealEstimateResult | null;
  loading?: boolean;
  /** Edit mode: a real Tiptap editor (locked model headings, :: drag-reorder, draft-on-type). */
  isEditMode?: boolean;
  /** The in-session edit draft (owned by DealWorkspace so it survives panel rebuilds). */
  draftDoc?: Record<string, unknown> | null;
  onDraftChange?: (doc: Record<string, unknown> | null) => void;
  /** View mode: hide unanswered questions (the question view's answered-only filter). */
  answeredOnly?: boolean;
  /**
   * Live answer write for the Notion-style property-panel groups (Company Overview / Primary
   * Contact). Same path the Questionnaire tab uses, so the two tabs stay in sync. Omitted = the
   * property rows render read-only (viewer).
   */
  onAnswerChange?: (fieldKey: string, value: unknown) => void;
  /**
   * Live-save a scope ITEM's scope_meta (status / activities) from its property rows - same immediate
   * merge path the Prioritization pane uses. Omitted (viewer) = those rows render read-only.
   */
  commitAdhocScopeFields?: (
    fieldKey: string,
    patch: Partial<
      Pick<
        ScopeMeta,
        'scopeStatus' | 'workType' | 'inScope' | 'outOfScope' | 'complexityDrivers' | 'assumptions'
      >
    >
  ) => void;
  /** Live editor handle for the host (collapse-all header button). Fired with null on unmount. */
  onEditorReady?: (editor: Editor | null) => void;
  /**
   * Remove request from a heading's hover x (scope items + functional areas). The host confirms
   * and deletes through the model API, then reloads - the doc itself is never edited. Omitted =
   * no x buttons.
   */
  onRemoveHeading?: (dataKey: string) => void;
}

/**
 * Scope tab: the questionnaire rendered as a Notion-style Tiptap document - H1 = section,
 * H2 = group / scope item, H3 = question - built by buildScopeDoc from the same scope model the
 * question view renders. View mode is a read-only projection; edit mode is a real editor whose
 * edits are harvested back into the model on Save (scope-doc-harvest + the one transactional
 * questionnaire-document endpoint). Model headings are locked (LockHeadings); ad-hoc scope items
 * get a :: drag grip (HeadingDragHandles) - moving one re-orders (and re-tags) it on save.
 */
export function DealScopePane({
  questionnaire,
  answers,
  fieldLookup,
  estimate = null,
  loading = false,
  isEditMode = false,
  draftDoc = null,
  onDraftChange,
  answeredOnly = true,
  onAnswerChange,
  commitAdhocScopeFields,
  onEditorReady,
  onRemoveHeading,
}: DealScopePaneProps) {
  const hoursByField = React.useMemo(() => buildHoursByField(estimate), [estimate]);

  // Live editor handle: drives the outline nav here and is forwarded to the host (collapse-all).
  const [editor, setEditor] = React.useState<Editor | null>(null);
  const handleEditorReady = React.useCallback(
    (ed: Editor | null) => {
      setEditor(ed);
      onEditorReady?.(ed);
    },
    [onEditorReady]
  );

  // Field lookup by fieldKey, so the property-panel node views can resolve a row's field from the
  // `fieldKey` carried on the propertyRow node.
  const fieldsByKey = React.useMemo(() => {
    const map = new Map<string, SalesDealTemplateFieldSummary>();
    for (const section of questionnaire?.sections ?? []) {
      for (const group of section.groups) {
        for (const field of group.fields) map.set(field.fieldKey, field);
      }
    }
    return map;
  }, [questionnaire]);

  // Suggestions for the item tag controls (In/Out scope, Complexity, Assumptions): the pool of every
  // value already used for that dimension anywhere on this deal, so the user can reuse a tag across
  // items. (A per-dimension AI "Suggest" + the driver library can layer on top later.)
  const tagPool = React.useMemo<Record<string, string[]>>(() => {
    const keys = ['inScope', 'outOfScope', 'complexityDrivers', 'assumptions'] as const;
    const sets: Record<string, Set<string>> = {
      inScope: new Set(),
      outOfScope: new Set(),
      complexityDrivers: new Set(),
      assumptions: new Set(),
    };
    for (const section of questionnaire?.sections ?? []) {
      for (const group of section.groups) {
        for (const f of group.fields) {
          const meta = f.scopeMeta as Record<string, unknown> | null | undefined;
          for (const k of keys) {
            const vals = meta?.[k];
            if (Array.isArray(vals)) {
              for (const v of vals) if (typeof v === 'string' && v.trim()) sets[k]!.add(v.trim());
            }
          }
        }
      }
    }
    const out: Record<string, string[]> = {};
    for (const k of keys) out[k] = [...sets[k]!].sort();
    return out;
  }, [questionnaire]);

  // Autocomplete machinery (Google Places address decomposition, entity/customer suggestions),
  // reusing the exact hook the Questionnaire tab uses so the panel's autocomplete fields behave
  // identically. Runs against the panel's live answer handler (or a noop for a read-only viewer).
  const noopAnswerChange = React.useCallback(() => {}, []);
  const {
    autocompleteValues,
    onAnswerChange: autocompleteOnAnswerChange,
    onPlaceSelect,
    onCustomerSelect,
    placesLoading,
  } = useQuestionnaireAutocomplete(questionnaire ?? null, onAnswerChange ?? noopAnswerChange);

  // Host bridge for the Notion property panel: renders each row's live control + reports emptiness.
  // Re-created when `answers` (or the live autocomplete suggestions) change so the node views (which
  // consume this via context) re-render live - the doc itself is unchanged (values aren't baked into
  // the doc JSON), so the editor never resets on a value edit.
  const scopeProperty = React.useMemo<ScopePropertyApi>(
    () => ({
      renderRow: (fieldKey) => {
        // A scope ITEM's own meta rows: `scopemeta:<primaryFieldKey>:<metaKey>` (status / activities /
        // hours) - live-saved to scope_meta, NOT the answer path used by the questionnaire fields.
        if (fieldKey.startsWith('scopemeta:')) {
          const [, fk, metaKey] = fieldKey.split(':');
          const field = fk ? fieldsByKey.get(fk) : undefined;
          return (
            <ScopeItemMetaRow
              metaKey={metaKey as ScopeItemMetaKey}
              scope={field?.scopeMeta ?? undefined}
              suggestions={metaKey ? (tagPool[metaKey] ?? []) : []}
              onCommit={
                commitAdhocScopeFields && fk
                  ? (patch) => commitAdhocScopeFields(fk, patch)
                  : undefined
              }
            />
          );
        }
        const field = fieldsByKey.get(fieldKey);
        if (!field) return null;
        // Editable: use the autocomplete-wrapped change handler (drives the Places search) and pass
        // the autocomplete context. Viewer (no onAnswerChange): read-only, no controls.
        return (
          <ScopePropertyRow
            field={field}
            answers={answers}
            onChange={onAnswerChange ? autocompleteOnAnswerChange : undefined}
            autocomplete={
              onAnswerChange
                ? { values: autocompleteValues, onPlaceSelect, onCustomerSelect, placesLoading }
                : undefined
            }
          />
        );
      },
      isRowEmpty: (fieldKey) => {
        // A scope item's meta rows are fixed properties - always shown (no Show-all collapse).
        if (fieldKey.startsWith('scopemeta:')) return false;
        const field = fieldsByKey.get(fieldKey);
        return field ? !fieldHasVisibleContent(field, answers) : true;
      },
    }),
    [
      fieldsByKey,
      answers,
      tagPool,
      commitAdhocScopeFields,
      onAnswerChange,
      autocompleteValues,
      autocompleteOnAnswerChange,
      onPlaceSelect,
      onCustomerSelect,
      placesLoading,
    ]
  );
  const builtDoc = React.useMemo(
    () =>
      questionnaire
        ? buildScopeDoc({
            questionnaire,
            answers,
            fieldLookup,
            hoursByField,
            mode: isEditMode ? 'edit' : 'view',
            answeredOnly,
            userSections: questionnaire.userSections,
          })
        : null,
    [questionnaire, answers, fieldLookup, hoursByField, isEditMode, answeredOnly]
  );
  // Model-anchored heading texts are read-only; user-added headings (new items) stay editable.
  const lockedHeadings = React.useMemo(() => collectScopeDocLockedHeadings(builtDoc), [builtDoc]);
  // The :: grip goes on ad-hoc scope ITEMS (H2 - reorder / re-tag by drop) AND functional-area
  // (Business-Process) SECTION H1s (drag the whole area to reorder it). The catch-all section and
  // template sections stay grip-less.
  const dragHandles = React.useCallback((dataKey: string | null) => {
    const anchor = parseScopeDocKey(dataKey);
    if (!anchor) return false;
    return (
      anchor.type === 'adhoc' ||
      (anchor.type === 'section' && isAdhocGroupSectionKey(anchor.key))
    );
  }, []);
  // Ad-hoc SECTIONS are drop CONTAINERS: items drop INTO them, and a dragged functional-area section
  // reorders among them. Dropping into a template section is rejected; an emptied/new ad-hoc section
  // can still receive items.
  const dragContainers = React.useCallback((dataKey: string | null) => {
    const anchor = parseScopeDocKey(dataKey);
    return anchor?.type === 'section' && isAdhocSectionKey(anchor.key);
  }, []);

  if (loading && !questionnaire) {
    return (
      <div className="deal-scope-doc deal-scope-doc--loading">
        <SkeletonBlock height="28px" width="40%" />
        <SkeletonBlock height="16px" width="90%" />
        <SkeletonBlock height="16px" width="75%" />
        <SkeletonBlock height="16px" width="85%" />
      </div>
    );
  }

  if (!builtDoc) {
    return (
      <div className="deal-scope-doc deal-scope-doc--empty">
        <p className="deal-scope-doc__empty-text">
          Nothing to show yet - fill in the questionnaire or run discovery to populate the deal.
        </p>
      </div>
    );
  }

  if (!isEditMode) {
    return (
      <div className="deal-scope-doc-shell">
        <ScopeDocOutline editor={editor} />
        <div className="deal-scope-doc">
          <RichTextEditor
            content={builtDoc}
            readOnly
            collapsibleHeadings
            scopeProperty={scopeProperty}
            onEditorReady={handleEditorReady}
          />
        </div>
      </div>
    );
  }

  // Edit mode: the draft (the editor's own JSON, set on every update) takes precedence so
  // model-driven rebuilds of `builtDoc` never clobber in-session edits (the SOW editor's
  // content-prop discipline).
  return (
    <div className="deal-scope-doc-shell">
      <ScopeDocOutline editor={editor} />
      <div className="deal-scope-doc deal-scope-doc--edit">
        <RichTextEditor
          content={draftDoc ?? builtDoc}
          readOnly={false}
          placeholder="Type to add detail, use / for commands (a Problem or Functional area is captured; headings are your own notes)"
          lockedHeadings={lockedHeadings}
          headingDragHandles={dragHandles}
          headingDragContainers={dragContainers}
          collapsibleHeadings
          scopeProperty={scopeProperty}
          customizeSlashMenu={buildScopeSlashMenu}
          // The removable set = the draggable set (scope items + functional areas).
          headingRemoveButtons={onRemoveHeading ? dragHandles : undefined}
          onHeadingRemove={onRemoveHeading}
          onUpdate={(json) => onDraftChange?.(json)}
          onEditorReady={handleEditorReady}
        />
      </div>
    </div>
  );
}
